import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { IColumnMetaData, IReportMetadata, ReportDataService } from '../report-data.service';
import { ICodeValue, IObject } from '../../../../type_definitions/datatypes';
import { DialogSize, GaMessageService, IDialog } from '../../message/ga-message.service';
import { TranslationService } from '../../translation/translation.service';
import { IReportExportModel, ReportExportComponent } from './report-export.component';
import { GaDeferredService, IGADeferred } from '../../services/ga-deferred.service';
import { util } from '../../util';


@Injectable()
export class ReportExportService {
  private reportId: string;
  private title: string;
  private metadata: IReportMetadata;
  private queryFilters: IObject;
  private exportReportDialog: IDialog;

  constructor(
    private messageService: GaMessageService,
    private reportDataService: ReportDataService,
    private translationService: TranslationService,
    private deferredService: GaDeferredService
  ) {
    /**/
  }

  public exportReport(reportId: number, metadata: IReportMetadata, queryFilters: IObject, title: string, overwriteExportCheckboxes: boolean, templateCode?: string): void {
    const template:ICodeValue = _.findWhere(metadata.templates, { code: templateCode });
    if (template) {
      this.reportDataService.getExcel(
        reportId,
        queryFilters,
        this.title,
        parseInt(template.id),
        metadata.columns,
        this.getSortedColumnIds(metadata.columns)
      );
    } else {
      // initially use 'defaultinexport' states that are defined in xml report
      // however, if the section 'Table Columns' was expanded or the loaded report is a stored report -> use 'defaultinlist' states (the same checkbox values as they appear under 'Table Columns' section)
      if (overwriteExportCheckboxes) {
        this.copyCheckboxesValues(metadata);
      }
      this.showExportReportDialog(reportId.toString(), title, metadata, queryFilters);
    }
  }

  public showExportReportDialog(reportId: string, title: string, metadata: IReportMetadata, queryFilters: any): void {
    this.reportId = reportId;
    this.title = title;
    this.metadata = metadata;
    this.queryFilters = queryFilters;

    this.showDialog()
      .then((exportModel: IReportExportModel) => {
        return this.doExport(exportModel);
      });
  }

  private showDialog(): Promise<IReportExportModel> {
    const deferred: IGADeferred<IReportExportModel> = this.deferredService.defer();

    const hasSubColumns:boolean = !!_.findWhere(this.metadata.columns, { hasSubColumns: true });
    const hasGroups = !!_.find(this.metadata.columns, (column: IColumnMetaData) => util.notNullOrUndefinedOrEmpty(column?.group));

    const closeDialog = (): void => {
      if (this.exportReportDialog) {
        this.exportReportDialog.hide();
      }
    };

    this.exportReportDialog = this.messageService.showDialog({
      component: ReportExportComponent,
      wrapperComponent: false,
      inputs: {
        reportId: this.reportId,
        title: `${this.translationService.instant('GA.BUTTONS.EXPORT')} ${this.title}`,
        metadata: this.metadata,
        queryFilters: this.queryFilters
      },
      outputs: {
        onExport: (model: IReportExportModel): void => {
          deferred.resolve(model);
          closeDialog();
        },
        onCancel: closeDialog
      },
      size: hasSubColumns? DialogSize.XL : (hasGroups? DialogSize.LG : undefined)
    });

    return deferred.promise;
  }

  private doExport(exportModel: IReportExportModel): Promise<void> {
    const exportedColumns:IColumnMetaData[] = exportModel.possibleColumns.filter((col) => col.formatting && col.formatting.defaultinexport);
    const sortedColumns:string = this.getSortedColumnIds(exportedColumns);
    return this.reportDataService.getExcel(
      this.reportId,
      this.queryFilters,
      this.title,
      exportModel.type && exportModel.type.id === 'excel'? undefined : exportModel.excelTemplate && <number>exportModel.excelTemplate.id,
      exportedColumns,
      sortedColumns
    );
  }

  public getSortedColumnIds(columns: IColumnMetaData[]):string {
    return _.chain(columns)
      .sortBy('listSequence')
      .pluck('id')
      .value()
      .toString();
  }

  // copy the state of column checkboxes from "Table Columns" and assign to "Export" column checkboxes
  private copyCheckboxesValues(metadata: IReportMetadata): void {
    _.each(metadata.columns, (column:IColumnMetaData) => {
      if (column.formatting && column.formatting.showinlist) {
        column.formatting.defaultinexport = column.formatting.defaultinlist;
      }
    });
  }
}